import React, { useCallback } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { LineItemRow } from './';
import { calculateGrandTotals, validateItems } from '../../utils/calculations';
import { MAX_LINE_ITEMS, VAT_RATE, createEmptyLineItem } from '../../constants';

// Shared with LineItemRow so the header and each row's columns always line
// up - kept in one place rather than duplicated magic strings that could
// drift out of sync.
// VAT % dropped as its own column: every row is always 23%, so it added a
// whole column of dead weight that squeezed Registration/Part No/Description
// on narrower screens for no informational benefit. Shown once, statically,
// below instead. Subtotal/VAT/Total also no longer render as full bordered
// TextFields (they're read-only anyway) - lighter-weight text needs less
// width and reads more clearly as "calculated", not "editable".
//
// Qty/Price/Discount/Subtotal/VAT/Total are all bounded-length values (a
// handful of digits, or one of ten fixed discount labels) - using `fr` for
// these scales them with whatever width happens to be available, wasting
// space on wider screens. Fixed px keeps them exactly as wide as their
// content needs; only the genuinely free-text columns stay flexible so they
// absorb whatever room is left over.
export const LINE_ITEM_GRID_COLUMNS = '1.1fr 1fr 1.8fr 64px 84px 100px 72px 72px 80px 44px';
export const LINE_ITEM_TABLE_MIN_WIDTH = 760;

const LineItemsTable = ({ items, onChange, onValidationChange, vatExempt = false }) => {
  const vatPercDisplay = vatExempt ? 0 : VAT_RATE * 100;

  // Calculate grand totals whenever items change
  React.useEffect(() => {
    const totals = calculateGrandTotals(items);
    const validation = validateItems(items);

    // Notify parent of totals and validation state
    if (onValidationChange) {
      onValidationChange({ totals, validation });
    }
  }, [items, onValidationChange]);

  const handleItemUpdate = useCallback((index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = updatedItem;
    onChange(newItems);
  }, [items, onChange]);

  const handleItemDelete = useCallback((index) => {
    if (items.length === 1) {
      // Don't delete if only one row, just clear it
      onChange([createEmptyLineItem()]);
    } else {
      const newItems = items.filter((_, i) => i !== index);
      onChange(newItems);
    }
  }, [items, onChange]);

  const canDeleteRow = items.length > 1;

  return (
    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
      {/* Horizontal scroll is the fallback for genuinely narrow viewports
          (smaller laptops, higher OS display-scaling) once the min-width
          below is reached, instead of columns silently truncating. */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: LINE_ITEM_TABLE_MIN_WIDTH }}>
          {/* Column Headers */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: LINE_ITEM_GRID_COLUMNS,
              gap: 1,
              mb: 1,
              px: 1,
            }}
          >
            <Typography variant="caption" fontWeight="bold">Registration</Typography>
            <Typography variant="caption" fontWeight="bold">Part No.</Typography>
            <Typography variant="caption" fontWeight="bold">Description</Typography>
            <Typography variant="caption" fontWeight="bold">Qty.</Typography>
            <Typography variant="caption" fontWeight="bold">Price</Typography>
            <Typography variant="caption" fontWeight="bold">Discount</Typography>
            <Typography variant="caption" fontWeight="bold" textAlign="right">Subtotal</Typography>
            <Typography variant="caption" fontWeight="bold" textAlign="right">VAT ({vatPercDisplay}%)</Typography>
            <Typography variant="caption" fontWeight="bold" textAlign="right">Total</Typography>
            <Box /> {/* Empty space for delete button column */}
          </Box>

          {/* Line Item Rows */}
          {items.map((item, index) => (
            <LineItemRow
              key={item._rowId ?? index}
              item={item}
              index={index}
              onUpdate={handleItemUpdate}
              onDelete={handleItemDelete}
              canDelete={canDeleteRow}
              vatExempt={vatExempt}
            />
          ))}
        </Box>
      </Box>

      {/* Info Text */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {items.length} / {MAX_LINE_ITEMS} rows
        </Typography>
        {!validateItems(items).ready && items.some(item => item.qty > 0 || item.price > 0) && (
          <Typography variant="caption" color="error">
            Please complete all fields for items with quantity or price
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default LineItemsTable;
